// graft(#97c): copied from genome/v4 sdk/src/plugins/define-plugin.ts — v5 owns this module, no live dep on v3/v4.
/**
 * `definePlugin()` — fluent builder for `ComposableGenomePlugin` descriptors.
 *
 * Combines plugin metadata and entity declarations into a single chain.
 * Entity IDs are auto-prefixed with `{pluginId}:`. Shorthand refs (strings
 * without ':') are auto-expanded: `'schema/alert'` becomes
 * `'{pluginId}:schema/alert'`.
 *
 * Editor registration: `.editor(id, component)` keys the editors map by the
 * passed id AND emits the matching `{pluginId}:component/{id}` entity.
 * Multiple `.editor()` calls register multiple editors; a duplicate id
 * throws. (v3's builder silently discarded the id, keyed everything by
 * pluginId, and silently overwrote on the second call.)
 *
 * `.layout()` (declarative panel composition) is deliberately absent until
 * the sdk/react surface layer exists — plugins pass a real component to
 * `.editor()` instead.
 *
 * @example
 * ```ts
 * export const monitoringPlugin = definePlugin('v4-monitoring')
 *   .name('Monitoring')
 *   .trustTier('core')
 *   .schema('alert', { type: 'object', properties: { id: { type: 'string' } } })
 *   .store('alerts', { validates: 'schema/alert', features: ['selection'] })
 *   .editor('canvas', MonitoringCanvas)
 *   .route('/monitoring', { routesTo: 'component/canvas', title: 'Monitoring' })
 *   .build();
 * ```
 *
 * @module @ubiclouds/v5-sdk/plugins/define-plugin
 */

import type { ComponentType } from 'react';
import type { EntityDef, EntityKind, EntityRef, RefRel } from '../genome/types.js';
import type { BackendContract, ComposableGenomePlugin, TrustTier } from './contract.js';

// ─── Public types ────────────────────────────────────────

export interface StoreOpts {
  /** Shorthand ref: `'schema/x'` auto-expands to `'{pluginId}:schema/x'` */
  readonly validates?: string;
  readonly features?: readonly string[];
  readonly initialState?: Readonly<Record<string, unknown>>;
}

export interface RouteOpts {
  /** Override the auto-derived route id */
  readonly id?: string;
  readonly title?: string;
  /** Shorthand ref: `'component/canvas'` auto-expands */
  readonly routesTo: string;
}

export interface ServiceOpts {
  readonly contract: string;
  readonly providers: readonly string[];
  /** Shorthand ref: `'schema/request'` auto-expands */
  readonly accepts?: string;
  /** Shorthand ref: `'schema/response'` auto-expands */
  readonly produces?: string;
}

export interface EventOpts {
  /** Shorthand ref to the payload schema */
  readonly payload?: string;
}

export interface ActionOpts {
  /** Shorthand ref: `'action/x'` or `'effect/y'` auto-expands */
  readonly triggers?: string;
}

export interface PluginBuilder {
  name(name: string): PluginBuilder;
  version(version: string): PluginBuilder;
  description(desc: string): PluginBuilder;
  trustTier(tier: TrustTier): PluginBuilder;
  capabilities(caps: readonly string[]): PluginBuilder;
  /** Declare additional namespaces this plugin contributes entities under. */
  contributesTo(namespaces: readonly string[]): PluginBuilder;

  schema(id: string, schema: Record<string, unknown>): PluginBuilder;
  store(id: string, opts?: StoreOpts): PluginBuilder;
  editor(id: string, component: ComponentType): PluginBuilder;
  route(path: string, opts: RouteOpts): PluginBuilder;
  service(id: string, opts: ServiceOpts): PluginBuilder;
  event(id: string, opts?: EventOpts): PluginBuilder;
  action(id: string, opts?: ActionOpts): PluginBuilder;

  backendContract(
    entityPath: string,
    interfaceName: string,
    providers: readonly string[],
  ): PluginBuilder;

  build(): ComposableGenomePlugin;
}

// ─── Ref expansion ───────────────────────────────────────

function expandRef(pluginId: string, shorthand: string): string {
  if (shorthand.includes(':')) return shorthand;
  return `${pluginId}:${shorthand}`;
}

function makeRef(pluginId: string, rel: RefRel, target: string): EntityRef {
  return { rel, target: expandRef(pluginId, target) };
}

// ─── Builder implementation ──────────────────────────────

export function definePlugin(pluginId: string): PluginBuilder {
  let _name: string = pluginId;
  let _version: string | undefined;
  let _description: string | undefined;
  // #1176/#1391/#1392 — DELIBERATELY undefined, not `'core'`. Emitting a default made
  // EVERY composed plugin an "adopter" of a field production reads NOWHERE (the only
  // consumer, `packages/store/src/install/trust-gate.ts`, reads the STORE MANIFEST's
  // `trustTier`, and imports this type as a TYPE only). That made the ledger row
  // structurally unsatisfiable: #1392 deleted the generated-cluster writes and took it
  // 25 -> 17, then #1391's single hand-authored plugin took it back to 18, because the
  // DEFAULT re-adopted it regardless of what anyone wrote. A row that can only ever
  // rise is #1231's permanent alarm, and the gate's own remedy is the one taken here —
  // "either wire a reader that fails without it, or stop writing it".
  let _trustTier: TrustTier | undefined;
  let _capabilities: readonly string[] | undefined;
  let _contributesTo: readonly string[] | undefined;
  const _editors: Record<string, ComponentType> = {};
  const _entities: EntityDef[] = [];
  let _backendContract: BackendContract | undefined;

  const builder: PluginBuilder = {
    name(n) {
      _name = n;
      return builder;
    },
    version(v) {
      _version = v;
      return builder;
    },
    description(d) {
      _description = d;
      return builder;
    },
    trustTier(t) {
      _trustTier = t;
      return builder;
    },
    capabilities(c) {
      _capabilities = c;
      return builder;
    },
    contributesTo(ns) {
      _contributesTo = ns;
      return builder;
    },

    schema(id, schema) {
      _entities.push({
        kind: 'schema' as EntityKind,
        schema,
        meta: { id: `${pluginId}:schema/${id}`, title: id },
      });
      return builder;
    },

    store(id, opts) {
      const refs: EntityRef[] = [];
      if (opts?.validates) {
        refs.push(makeRef(pluginId, 'validates', opts.validates));
      }
      _entities.push({
        kind: 'store' as EntityKind,
        meta: {
          id: `${pluginId}:store/${id}`,
          features: opts?.features ? [...opts.features] : undefined,
          initialState: opts?.initialState,
        },
        ...(refs.length > 0 ? { refs } : {}),
      });
      return builder;
    },

    editor(id, component) {
      if (_editors[id]) {
        throw new Error(
          `definePlugin("${pluginId}").editor: duplicate editor id "${id}" — each editor needs a distinct component entity id`,
        );
      }
      _editors[id] = component;
      _entities.push({
        kind: 'component' as EntityKind,
        meta: { id: `${pluginId}:component/${id}` },
      });
      return builder;
    },

    route(path, opts) {
      const routeId =
        opts.id ?? (path.replace(/^\//, '').replace(/\//g, '-') || 'root');
      const refs: EntityRef[] = [
        makeRef(pluginId, 'routes-to', opts.routesTo),
      ];
      _entities.push({
        kind: 'route' as EntityKind,
        meta: {
          id: `${pluginId}:route/${routeId}`,
          path,
          title: opts.title,
        },
        refs,
      });
      return builder;
    },

    service(id, opts) {
      const refs: EntityRef[] = [];
      if (opts.accepts) {
        refs.push(makeRef(pluginId, 'accepts', opts.accepts));
      }
      if (opts.produces) {
        refs.push(makeRef(pluginId, 'produces', opts.produces));
      }
      _entities.push({
        kind: 'service' as EntityKind,
        meta: {
          id: `${pluginId}:service/${id}`,
          contract: opts.contract,
          availableProviders: [...opts.providers],
        },
        ...(refs.length > 0 ? { refs } : {}),
      });
      return builder;
    },

    event(id, opts) {
      const refs: EntityRef[] = [];
      if (opts?.payload) {
        refs.push(makeRef(pluginId, 'produces', opts.payload));
      }
      _entities.push({
        kind: 'event' as EntityKind,
        meta: { id: `${pluginId}:event/${id}` },
        ...(refs.length > 0 ? { refs } : {}),
      });
      return builder;
    },

    action(id, opts) {
      const refs: EntityRef[] = [];
      if (opts?.triggers) {
        refs.push(makeRef(pluginId, 'triggers', opts.triggers));
      }
      _entities.push({
        kind: 'action' as EntityKind,
        meta: { id: `${pluginId}:action/${id}` },
        ...(refs.length > 0 ? { refs } : {}),
      });
      return builder;
    },

    backendContract(entityPath, interfaceName, providers) {
      _backendContract = {
        entityPath,
        interfaceName,
        providers: [...providers],
      };
      return builder;
    },

    build(): ComposableGenomePlugin {
      const frozenEditors = Object.freeze({ ..._editors });
      const frozenEntities = Object.freeze(_entities.map((e) => Object.freeze(e)));

      const plugin: ComposableGenomePlugin = {
        id: pluginId,
        name: _name,
        ...(_version != null ? { version: _version } : {}),
        ...(_description != null ? { description: _description } : {}),
        ...(_trustTier != null ? { trustTier: _trustTier } : {}),
        ...(_capabilities != null ? { capabilities: [..._capabilities] } : {}),
        ...(_contributesTo != null ? { contributesTo: [..._contributesTo] } : {}),
        editors: frozenEditors,
        entities(): EntityDef[] {
          return [...frozenEntities];
        },
        ...(_backendContract != null ? { backendContract: _backendContract } : {}),
      };

      return Object.freeze(plugin);
    },
  };

  return builder;
}
